import React, { useState, useEffect, useMemo } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  EvaluationCard,
  SummaryMetricCard,
  PerformanceIndicatorBar,
  RecommendationBanner,
  ActionOptionCard,
  WarningBanner,
  EvaluationFooter,
  SwapArrowsIcon,
  ClipboardCheckIcon,
  PencilLineIcon,
} from "./components";
import {
  FLUENCY_LEVELS,
  COMPREHENSION_LEVELS,
  getStudentFullName,
} from "./constants";
import { findById } from "../../../Mapper/StudentMapper";

const PERFORMANCE_LABELS = ["ضعيف", "محدود", "جيد", "ممتاز"];

const DECISION_TYPES = {
  CONCLUSIVE: "conclusive",
  INCONCLUSIVE: "inconclusive",
};

const INCONCLUSIVE_RECOMMENDATION_ACTIONS = new Set(["reevaluate", "reassess"]);

const getLevelId = (level) => level?.id ?? level?._id;

const normalizeAction = (action = "") =>
  action.toLowerCase().replace(/[\s_-]/g, "");

const isInconclusiveRecommendation = (action) =>
  INCONCLUSIVE_RECOMMENDATION_ACTIONS.has(normalizeAction(action));

const findLevelById = (levels = [], levelId) => {
  if (levelId == null) return undefined;

  return levels.find((level) => {
    const id = getLevelId(level);
    return id != null && String(id) === String(levelId);
  });
};

const isSameLevel = (firstLevel, secondLevel) => {
  const firstId = getLevelId(firstLevel);
  const secondId = getLevelId(secondLevel);

  return (
    firstId != null && secondId != null && String(firstId) === String(secondId)
  );
};

const getRecommendationMessage = (action, levelCode = "-") => {
  switch (normalizeAction(action)) {
    case "reevaluate":
    case "reassess":
      return `إعادة تقييم الطالب على المستوى (${levelCode}) للحصول على نتيجة أكثر دقة.`;
    case "promote":
      return `الترفيع إلى المستوى ${levelCode}`;
    case "demote":
      return `التخفيض إلى المستوى ${levelCode}`;
    case "keep":
      return `الإبقاء في المستوى ${levelCode}`;
    default:
      return `المستوى الموصى به: ${levelCode}`;
  }
};

const getAccuracyIndicator = (percent) => {
  if (percent < 90) {
    return {
      accuracyLabel: "بحاجة إلى مساعدة",
      accuracyPillBg: "#fee2e2",
      accuracyPillColor: "#991b1b",
    };
  }
  if (percent < 95) {
    return {
      accuracyLabel: "جيد",
      accuracyPillBg: "#fef3c7",
      accuracyPillColor: "#92400e",
    };
  }
  return {
    accuracyLabel: "ممتاز",
    accuracyPillBg: "#dcfce7",
    accuracyPillColor: "#166534",
  };
};

const AssessmentResultsStep = ({
  sessionsData,
  studentsData,
  levelsData = [],
  classroomsData,
  sectionsData,
  totalWords = 0,
  incorrectWords = 0,
  accuracyPercent = 0,
  fluencyScore = 0,
  comprehensionScore = 0,
  onDecide,
  isSaving,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const temporaryLevels = sessionsData?.temporaryLevels;
  const currentDecisionLevel = temporaryLevels?.current;
  const expectedLevel = temporaryLevels?.expected;
  const nextTextLevel = temporaryLevels?.nextText;
  const recommendation = sessionsData?.results?.recommendation;
  const recommendationLevel = useMemo(
    () => findLevelById(levelsData, recommendation?.levelId),
    [levelsData, recommendation?.levelId],
  );
  const recommendedDecisionType = isInconclusiveRecommendation(
    recommendation?.action,
  )
    ? DECISION_TYPES.INCONCLUSIVE
    : DECISION_TYPES.CONCLUSIVE;

  const [selectedLevel, setSelectedLevel] = useState(currentDecisionLevel);
  const [selectedDecisionType, setSelectedDecisionType] = useState(
    recommendedDecisionType,
  );

  useEffect(() => {
    setSelectedLevel(currentDecisionLevel);
  }, [currentDecisionLevel]);

  useEffect(() => {
    setSelectedDecisionType(recommendedDecisionType);
  }, [recommendedDecisionType]);

  const studentName = getStudentFullName(studentsData) || "-";
  const studentLevelObj = useMemo(
    () => findById(levelsData, studentsData?.levels?.current?._id),
    [levelsData, studentsData],
  );
  const studentLevelCode =
    studentLevelObj?.code || studentsData?.levels?.current?.code || "-";

  const className = classroomsData?.name?.ar || "";
  const sectionName = sectionsData?.name?.ar || "";
  const classSectionDisplay =
    className && sectionName
      ? `${className} - ${sectionName}`
      : className || sectionName || "-";

  const duration = sessionsData?.reading?.duration || 0;
  const correctWords = Math.max(0, totalWords - incorrectWords);
  const readingRate =
    duration > 0 ? Math.round((correctWords / duration) * 60) : 0;

  const toggleDropdown = (e) => {
    e?.stopPropagation();
    setDropdownOpen((prevState) => !prevState);
  };

  const { accuracyLabel, accuracyPillBg, accuracyPillColor } = useMemo(
    () => getAccuracyIndicator(accuracyPercent),
    [accuracyPercent],
  );

  const fluencyLabel =
    (FLUENCY_LEVELS[fluencyScore] || FLUENCY_LEVELS[0])?.label || "جيد";

  const comprehensionLabel =
    (
      COMPREHENSION_LEVELS.find((l) => l.check(comprehensionScore)) ||
      COMPREHENSION_LEVELS[2]
    )?.label || "جيد";

  const fluencyPercent = Math.min(
    100,
    Math.max(25, ((fluencyScore + 1) / 4) * 100),
  );
  const comprehensionPercent = Math.min(
    100,
    Math.max(25, (comprehensionScore / 9) * 100),
  );

  const suggestedActionText = getRecommendationMessage(
    recommendation?.action,
    recommendationLevel?.code,
  );

  const handleConfirm = () => {
    const isConclusive = selectedDecisionType === DECISION_TYPES.CONCLUSIVE;
    const levelId = getLevelId(selectedLevel);

    if (isConclusive && levelId == null) return;

    const decision = isConclusive
      ? { type: DECISION_TYPES.CONCLUSIVE, levelId }
      : { type: DECISION_TYPES.INCONCLUSIVE };

    onDecide?.(decision);
  };

  const isLevelMatchingRecommended = isSameLevel(
    selectedLevel,
    recommendationLevel,
  );

  const isConclusiveRecommended =
    recommendedDecisionType === DECISION_TYPES.CONCLUSIVE &&
    isLevelMatchingRecommended;

  const isWarningShown =
    selectedDecisionType !== recommendedDecisionType ||
    (selectedDecisionType === DECISION_TYPES.CONCLUSIVE &&
      !isLevelMatchingRecommended);

  const dropdownColor =
    selectedDecisionType === DECISION_TYPES.CONCLUSIVE
      ? isConclusiveRecommended
        ? "#15803D"
        : "#C2410C"
      : "#64748B";

  const dropdownBorder =
    selectedDecisionType === DECISION_TYPES.CONCLUSIVE
      ? isConclusiveRecommended
        ? "1px solid #16A34A"
        : "1px solid #EA580C"
      : "1px solid #CBD5E1";

  const isConfirmDisabled =
    selectedDecisionType === DECISION_TYPES.CONCLUSIVE &&
    getLevelId(selectedLevel) == null;

  return (
    <div
      className="mx-auto"
      style={{
        maxWidth: "860px",
        width: "100%",
        direction: "rtl",
        paddingBottom: "100px",
      }}
    >
      <EvaluationCard className="mb-4">
        <h4
          className="fw-bold text-dark text-center mb-4"
          style={{ fontSize: "22px" }}
        >
          نتائج التقييم
        </h4>

        <div className="d-flex justify-content-around align-items-center text-center flex-wrap gap-3">
          <div className="flex-fill">
            <div className="text-muted mb-1" style={{ fontSize: "14px" }}>
              اسم الطالب
            </div>
            <div className="fw-bold text-dark" style={{ fontSize: "16px" }}>
              {studentName}
            </div>
          </div>

          <div
            className="border-start border-end flex-fill px-3"
            style={{ borderColor: "#e2e8f0" }}
          >
            <div className="text-muted mb-1" style={{ fontSize: "14px" }}>
              الصف
            </div>
            <div className="fw-bold text-dark" style={{ fontSize: "16px" }}>
              {classSectionDisplay}
            </div>
          </div>

          <div className="flex-fill">
            <div className="text-muted mb-1" style={{ fontSize: "14px" }}>
              المستوى الحالي
            </div>
            <div className="fw-bold text-dark" style={{ fontSize: "16px" }}>
              {studentLevelCode}
            </div>
          </div>
        </div>
      </EvaluationCard>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <EvaluationCard
            title="ملخص القراءة"
            subtitle="المقاييس الأساسية من تقييم القراءة"
            className="h-100"
          >
            <div className="row g-2 mb-3">
              <div className="col-6">
                <SummaryMetricCard
                  title="عدد الكلمات غير الصحيحة"
                  value={`${incorrectWords} كلمات`}
                />
              </div>
              <div className="col-6">
                <SummaryMetricCard
                  title="إجمالي عدد الكلمات"
                  value={`${totalWords} كلمة`}
                />
              </div>
            </div>

            <SummaryMetricCard
              title="معدل القراءة في الدقيقة"
              value={`${readingRate} كلمة`}
              className="mt-auto"
              fontSize="22px"
            />
          </EvaluationCard>
        </div>

        <div className="col-md-6">
          <EvaluationCard
            title="مؤشرات الأداء"
            subtitle="الدقة والطلاقة والفهم بنظرة سريعة"
            className="h-100"
          >
            <PerformanceIndicatorBar
              title="معدل دقة القراءة"
              badgeText={accuracyLabel}
              badgeBg={accuracyPillBg}
              badgeColor={accuracyPillColor}
              percent={accuracyPercent}
              labels={["بحاجة إلى مساعدة", "مستقل"]}
            />

            <PerformanceIndicatorBar
              title="الطلاقة"
              badgeText={fluencyLabel}
              badgeBg="#e0f2fe"
              badgeColor="#0369a1"
              percent={fluencyPercent}
              labels={PERFORMANCE_LABELS}
            />

            <PerformanceIndicatorBar
              title="الفهم والتحليل"
              badgeText={comprehensionLabel}
              badgeBg="#fef3c7"
              badgeColor="#b45309"
              percent={comprehensionPercent}
              labels={PERFORMANCE_LABELS}
            />
          </EvaluationCard>
        </div>
      </div>

      <RecommendationBanner message={suggestedActionText} />

      <div className="position-relative my-4 text-center">
        <hr style={{ borderColor: "#e2e8f0" }} />
        <span
          className="position-absolute top-50 start-50 translate-middle px-3 text-muted bg-white"
          style={{ fontSize: "14px" }}
        >
          اختر الاجراء المناسب
        </span>
      </div>

      <div className="row g-3 mb-3">
        <ActionOptionCard
          title="اعتماد التقييم الحالي"
          subtitle={
            <div className="d-flex align-items-center flex-wrap gap-2 text-muted mt-1">
              <span
                style={{
                  fontSize: "13px",
                  color:
                    selectedDecisionType === DECISION_TYPES.CONCLUSIVE
                      ? "#64748B"
                      : "#94A3B8",
                }}
              >
                سيتم تعديل مستوى الطالب الى المستوى
              </span>
              <Dropdown
                isOpen={dropdownOpen}
                toggle={toggleDropdown}
                className="d-inline-block"
              >
                <DropdownToggle
                  tag="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDecisionType(DECISION_TYPES.CONCLUSIVE);
                  }}
                  className="btn btn-sm bg-white px-2 py-0 fw-bold d-inline-flex align-items-center gap-1 ms-1"
                  style={{
                    border: dropdownBorder,
                    borderRadius: "6px",
                    fontSize: "13px",
                    color: dropdownColor,
                    height: "26px",
                  }}
                >
                  <span className="ms-1">{selectedLevel?.code || "-"}</span>
                  <PencilLineIcon size={14} color={dropdownColor} />
                </DropdownToggle>
                <DropdownMenu
                  end
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {levelsData?.map((lvl) => (
                    <DropdownItem
                      key={lvl.id || lvl._id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLevel(lvl);
                        setSelectedDecisionType(DECISION_TYPES.CONCLUSIVE);
                        setDropdownOpen(false);
                      }}
                      active={isSameLevel(selectedLevel, lvl)}
                    >
                      المستوى {lvl.code}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          }
          icon={<ClipboardCheckIcon size={22} />}
          isSelected={selectedDecisionType === DECISION_TYPES.CONCLUSIVE}
          isRecommended={isConclusiveRecommended}
          onClick={() => setSelectedDecisionType(DECISION_TYPES.CONCLUSIVE)}
        />
        <ActionOptionCard
          title="إعادة تقييم الطالب"
          subtitle={
            <div className="text-muted mb-0" style={{ fontSize: "13px" }}>
              <div>
                يجب إعادة تقييم الطالب على المستوى:{" "}
                <strong>{nextTextLevel?.code || "-"}</strong>
              </div>
              <div>
                مستوى الطالب المتوقع:{" "}
                <strong>{expectedLevel?.code || "-"}</strong>
              </div>
            </div>
          }
          icon={<SwapArrowsIcon size={22} />}
          isSelected={selectedDecisionType === DECISION_TYPES.INCONCLUSIVE}
          isRecommended={
            recommendedDecisionType === DECISION_TYPES.INCONCLUSIVE
          }
          onClick={() => setSelectedDecisionType(DECISION_TYPES.INCONCLUSIVE)}
        />
      </div>

      {isWarningShown && (
        <WarningBanner message="هذا الخيار يخالف الاجراء المقترح وقد ينتج عنه نتائج لا تعكس المستوى الحقيقي للطالب" />
      )}

      <EvaluationFooter
        primaryText="تأكيد"
        onPrimaryClick={handleConfirm}
        isSaving={isSaving}
        disabled={isConfirmDisabled}
      />
    </div>
  );
};

export default AssessmentResultsStep;
